import React from 'react';
import CONSTANTS, { EditorConstants, ContentTypeConstants, normalizeEditorKey } from '../../constants/contentCreationConstants';

const EDITOR_OPTIONS = Object.values(EditorConstants);

const TopicSelector = ({
  topics,
  subTopics,
  selectedTopic,
  selectedSubTopic,
  contentType,
  count,
  handleTopicChange,
  handleSubTopicChange,
  handleContentTypeChange,
  isContentTypeDisabled,
  freezeFilters = false,
  isFromProjectContentBank = false,
  selectedEditor,
  setSelectedEditorWithSession,
  currentFile
}) => {
  // Debug logging
  React.useEffect(() => {
    // Debug tracking removed - no action needed
  }, [freezeFilters, isContentTypeDisabled, contentType, selectedTopic, selectedSubTopic, topics]);

  // Validate that selectedTopic exists in topics array, otherwise use empty string
  const validSelectedTopic = selectedTopic && topics.length > 0 
    ? (topics.some(topic => String(topic.topic_id) === String(selectedTopic)) ? selectedTopic : "")
    : selectedTopic;

  // Validate that selectedSubTopic exists in subTopics array, otherwise use empty string
  const validSelectedSubTopic = selectedSubTopic && subTopics.length > 0
    ? (subTopics.some(subTopic => String(subTopic.subtopic_id) === String(selectedSubTopic)) ? selectedSubTopic : "")
    : selectedSubTopic;

  return (
    <div>
      <div className="container-fluid">
        <div className="row p-0">
          <div className="col-6 p-1">
            <label htmlFor="concept" className="form-label">
              Choose Topic :
            </label>
            <select
              onChange={handleTopicChange}
              className="form-select custom-input"
              value={validSelectedTopic || ""}
              disabled={freezeFilters === true}
            >
              <option value="">Select a Topic</option>
              {topics.map((topic) => (
                <option key={topic.topic_id} value={topic.topic_id}>
                  {topic.topic_name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-6 p-1 d-flex align-items-center justify-content-center">
            {!isFromProjectContentBank && (
              <h4>Total Questions : {(contentType===ContentTypeConstants.CODING.key)?count.coding:(contentType===ContentTypeConstants.MCQ.key || contentType===ContentTypeConstants.QUIZ.key)?count.mcq:count.total}</h4>
            )}
          </div>
        </div>
      </div>
      <div className="container-fluid">
        <div className="row p-0">
          <div className="col-6 p-1">
            <label htmlFor="concept" className="form-label">
              Choose Sub Topic :
            </label>
            <select
              disabled={!validSelectedTopic || (freezeFilters === true)}
              onChange={handleSubTopicChange}
              className="form-select custom-input"
              value={validSelectedSubTopic || ""}
            >
              <option value="">Select a Subtopic</option>
              {subTopics.map((subTopic) => (
                <option
                  key={subTopic.subtopic_id}
                  value={subTopic.subtopic_id}
                >
                  {subTopic.subtopic_name}
                </option>
              ))}
            </select>
          </div>
          <div className={`p-1 ${(contentType === ContentTypeConstants.CODING.key || contentType === ContentTypeConstants.CODING.label) ? 'col-3' : 'col-6'}`}>
      <label htmlFor="chooseContentType" className="form-label">
        Choose Type :
      </label>

      {(() => {
        const access = sessionStorage.getItem('access') || '';
        const hasMCQ = access.includes(ContentTypeConstants.MCQ.key);
        const hasQuiz = access.includes(ContentTypeConstants.QUIZ.key);
        const hasCoding = access.includes(ContentTypeConstants.CODING.key);
        const hasProjectCoding = access.includes(ContentTypeConstants.PROJECT_CODING.key);
        const hasVideo = access.includes(ContentTypeConstants.VIDEO.key);
        const hasNotes = access.includes(ContentTypeConstants.NOTES.key);

        const availableOptions = [];
        if (hasMCQ) availableOptions.push({ value: ContentTypeConstants.MCQ.key, label: ContentTypeConstants.MCQ.label });
        if (hasQuiz || hasMCQ) {
          availableOptions.push({
            value: ContentTypeConstants.QUIZ.key,
            label: ContentTypeConstants.QUIZ.label,
          });
        }
        if (hasCoding) availableOptions.push({ value: ContentTypeConstants.CODING.key, label: ContentTypeConstants.CODING.label });
        if (hasProjectCoding) availableOptions.push({ value: ContentTypeConstants.PROJECT_CODING.key, label: ContentTypeConstants.PROJECT_CODING.label });
        if (hasVideo) availableOptions.push({ value: ContentTypeConstants.VIDEO.key, label: ContentTypeConstants.VIDEO.label });
        if (hasNotes) availableOptions.push({ value: ContentTypeConstants.NOTES.key, label: ContentTypeConstants.NOTES.label });

        const normalizeContentType = (type) => {
          if (!type || type === 'default') return type;
          // Check against all content type keys and labels
          const contentTypeMap = {
            [ContentTypeConstants.VIDEO.key]: ContentTypeConstants.VIDEO.key,
            [ContentTypeConstants.NOTES.key]: ContentTypeConstants.NOTES.key,
            [ContentTypeConstants.MCQ.key]: ContentTypeConstants.MCQ.key,
            [ContentTypeConstants.QUIZ.key]: ContentTypeConstants.QUIZ.key,
            [ContentTypeConstants.CODING.key]: ContentTypeConstants.CODING.key,
            [ContentTypeConstants.PROJECT_CODING.key]: ContentTypeConstants.PROJECT_CODING.key
          };
          return contentTypeMap[type] || type;
        };

        const normalizedContentType = normalizeContentType(contentType);
        let isValid = availableOptions.some(opt => opt.value === normalizedContentType);
        // When freezeFilters (e.g. from ProjectContentBank), ensure current type displays even if not in access
        if (!isValid && freezeFilters && normalizedContentType) {
          const match = Object.values(ContentTypeConstants).find((c) => c.key === normalizedContentType);
          if (match) {
            availableOptions.push({ value: normalizedContentType, label: match.label });
            isValid = true;
          }
        }
        const validContentType = isValid ? normalizedContentType : 'default';

        return (
          <select
            className="form-select custom-input"
            id="content_type"
            value={validContentType}
            onChange={handleContentTypeChange}
            disabled={isContentTypeDisabled || (freezeFilters === true)}
              title={freezeFilters === true ? "Content type is frozen from project configuration" : ""}
              style={freezeFilters === true ? { backgroundColor: '#e9ecef', opacity: 1 } : {}}
          >
            <option value="default">Select a Type</option>
                  {availableOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      })()}
    </div>

    {(contentType === ContentTypeConstants.CODING.key ||
      contentType === ContentTypeConstants.CODING.label) && (
      <div className="col-3 p-1">
        <label htmlFor="chooseEditor" className="form-label">
          Choose Editor :
        </label>
        {(() => {
          const normalizedEditor = normalizeEditorKey(selectedEditor);
          // Keep dropdown usable when editor is still unset (e.g. question preloaded from Content Bank).
          // Only lock after an editor is chosen for an existing question, or when filters are frozen.
          const editorLocked =
            freezeFilters ||
            (currentFile !== CONSTANTS.DEFAULT_QN && Boolean(normalizedEditor));
          return (
        <select
          className="form-select custom-input"
          id="chooseEditor"
          value={normalizedEditor}
          onChange={(e) => setSelectedEditorWithSession(e.target.value)}
          disabled={editorLocked}
          title={
            freezeFilters
              ? "Editor is frozen from project configuration"
              : editorLocked
                ? "Editor is locked for the selected question"
                : ""
          }
          style={editorLocked ? { backgroundColor: '#e9ecef', opacity: 1 } : {}}
        >
          <option value="">Select an Editor</option>
          {EDITOR_OPTIONS.map((editor) => (
            <option key={editor.key} value={editor.key}>
              {editor.label}
            </option>
          ))}
        </select>
          );
        })()}
      </div>
    )}

  </div>
</div>

        </div>

  );
};

export default TopicSelector; 
